// «Изменить поле» for the selected records (programme В8 — curators for many talks at once, and any other section): one value
// for one field, sent record by record, with the result of each — a record the server refuses is named with its reason. The bulk
// actions of «Мои» (R-U30) are the same dialog narrowed to one field, the owner of the record, or a fixed change («Выполнить»).

import { useState } from 'react';
import { canWrite, peopleFields, plural, recordTitle, type FieldDef, type Id } from '@confdeck/core-shared';
import { RecordPicker, TermPicker, UserPicker } from '../fields/pickers.tsx';
import { useAccess, useMeta, useSync } from '../sync/react.tsx';
import { Button, Checkbox, Dialog, Input } from '../ui/primitives.tsx';
import { DateInput } from '../ui/DateInput.tsx';
import { applyEach, type Refused } from './bulk-apply.ts';
import s from './import.module.css';

/** Kinds one value can be given to many records; relations, files and computed fields are edited one by one. */
const BULK_KINDS = new Set(['taxonomy', 'user', 'checkbox', 'text', 'number', 'date', 'email', 'url', 'relation']);

export function bulkFields(fields: FieldDef[]): FieldDef[] {
  // A relation is changed from its own side: the other side's list (a talk's reviews, its slots) is someone else's field.
  return fields.filter((f) => !f.archivedAt && !f.options.managed && BULK_KINDS.has(f.fieldType) && !(f.fieldType === 'relation' && f.options.side === 'to'));
}

/** The owner of a record (`ownerId`, «Ответственный»): changed for many like a person field. */
const OWNER = { id: 'ownerId', key: 'ownerId', name: 'Ответственный', fieldType: 'user', options: {} } as unknown as FieldDef;

export function BulkEdit({ typeId, ids, onClose, only, preset, title }: {
  typeId: Id; ids: Id[]; onClose: () => void;
  /** The fields to offer, in this order; `ownerId` — the record's owner. */
  only?: string[];
  /** A change with nothing to choose (close the task as done): the data of each record, or null when it has nothing to change. */
  preset?: { label: string; data: (id: Id) => Record<string, unknown> | Refused | null };
  title?: string;
}) {
  const meta = useMeta();
  const client = useSync();
  const access = useAccess();
  // People and labels first: the usual change of many records (curators, a status); relations last.
  const rank = (f: FieldDef) => (f.fieldType === 'user' ? 0 : f.fieldType === 'taxonomy' ? 1 : f.fieldType === 'relation' ? 3 : 2);
  const all = bulkFields(peopleFields(meta.fields(typeId))).filter((f) => f.key !== meta.type(typeId)?.titleField).sort((a, b) => rank(a) - rank(b));
  const fields = only ? only.map((k) => (k === 'ownerId' ? OWNER : all.find((f) => f.key === k))).filter((f): f is FieldDef => !!f) : all;
  const [key, setKey] = useState(fields[0]?.key ?? '');
  const [value, setValue] = useState<unknown>(null);
  const [busy, setBusy] = useState<{ done: number } | null>(null);
  const [result, setResult] = useState<{ ok: number; same: number; failed: { title: string; reason: string }[] } | null>(null);
  const field = fields.find((f) => f.key === key);
  if (!canWrite(access, typeId)) return null;

  const apply = async () => {
    if (!field && !preset) return;
    setBusy({ done: 0 });
    const input = (id: string) => {
      if (preset) { const data = preset.data(id); return data === null || 'refuse' in data ? data : { id, data }; }
      return field!.key === 'ownerId' ? { id, ownerId: value } : { id, data: { [field!.key]: value } };
    };
    const r = await applyEach(ids, input, (i) => client.run('record.update', i, { ask: false }), (done) => setBusy({ done }));
    setBusy(null);
    setResult({ ok: r.ok, same: r.same, failed: r.failed.map((f) => ({ title: recordTitle(client.store.record(f.id), meta), reason: f.reason })) });
  };

  const editor = (() => {
    if (!field) return null;
    switch (field.fieldType) {
      case 'taxonomy': return <TermPicker field={field} value={value} onChange={setValue} />;
      case 'user': return field.options.multiple ? <UserPicker value={value} onChangeMany={setValue} /> : <UserPicker value={value} onChange={setValue} />;
      case 'relation': return <RecordPicker field={field} value={Array.isArray(value) ? (value as Id[]) : []} onChange={setValue} />;
      case 'checkbox': return <Checkbox checked={value === true} onChange={setValue}>{field.name}</Checkbox>;
      case 'number': return <Input type="number" value={value === null ? '' : String(value)} onChange={(e) => setValue(e.target.value === '' ? null : Number(e.target.value))} />;
      case 'date': return <DateInput value={String(value ?? '')} onChange={(v) => setValue(v || null)} />;
      default: return <Input value={String(value ?? '')} onChange={(e) => setValue(e.target.value || null)} />;
    }
  })();

  return (
    <Dialog open onOpenChange={(o) => !o && !busy && onClose()} label="Изменить поле">
      <div className={s.box}>
        <header className={s.head}><span className={s.headTitle}>{title ?? 'Изменить поле'} у {ids.length} {ids.length === 1 ? 'записи' : 'записей'}</span></header>
        {!result && preset ? (
          <p className={s.summary}>{preset.label}: {ids.length} {plural(ids.length, 'запись', 'записи', 'записей')}. Каждая меняется отдельно — отказ по одной не останавливает остальные.</p>
        ) : !result ? (
          <>
            {fields.length > 1 && <div className={s.bulkRow}><span>Поле</span>
              <span className={s.bulkFields}>{fields.map((f) => <Button key={f.id} size="sm" variant={f.key === key ? 'primary' : 'secondary'} disabled={!!busy} onClick={() => { setKey(f.key); setValue(null); }}>{f.name}</Button>)}</span>
            </div>}
            <div className={s.bulkEditor}>{editor}</div>
            <p className={s.summary}>{fields.length === 1 ? `«${fields[0]!.name}» — ` : ''}пустое значение очистит поле у всех выбранных.</p>
          </>
        ) : (
          <div className={s.summary}>
            <div>Изменено: <b>{result.ok}</b> из {ids.length}</div>
            {result.same > 0 && <div>Без изменений: <b>{result.same}</b> — уже так</div>}
            {result.failed.length > 0 && <div className={s.errors}><b>Не изменены ({result.failed.length}):</b>{result.failed.slice(0, 20).map((f, i) => <div key={i}>{f.title}: {f.reason}</div>)}</div>}
          </div>
        )}
        <footer className={s.foot}>
          {busy && <span className={s.summary}>Изменено {busy.done} из {ids.length}…</span>}
          {!result ? (
            <>
              <Button onClick={onClose} disabled={!!busy}>Отмена</Button>
              <Button variant="primary" disabled={(!field && !preset) || !!busy} onClick={() => void apply()}>{preset?.label ?? 'Изменить'}</Button>
            </>
          ) : <Button variant="primary" onClick={onClose}>Закрыть</Button>}
        </footer>
      </div>
    </Dialog>
  );
}
